import { useState } from 'react';
import { ExternalLink, ChevronDown, Zap, Calculator } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createParlayLink, BOOKMAKERS } from '../../services/oddsApiClient';

const ParlayExporter = ({ bets, dropdownDirection = 'up' }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showBookmakerMenu, setShowBookmakerMenu] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBookmaker, setSelectedBookmaker] = useState('fanduel');
  const isParlay = bets.length > 1;

  // Calculate parlay odds for selected bookmaker
  const calculateParlayOdds = (bookmakerKey) => {
    if (bets.length < 2) return null;

    const odds = bets
      .map(bet => bet.parsedData?.bookmakers?.[bookmakerKey]?.price)
      .filter(price => price !== undefined && price !== null);

    if (odds.length !== bets.length) return null;

    // Convert American odds to decimal and multiply
    const decimalOdds = odds.map(americanOdds => {
      if (americanOdds > 0) {
        return (americanOdds / 100) + 1;
      } else {
        return (100 / Math.abs(americanOdds)) + 1;
      }
    });

    const combinedDecimal = decimalOdds.reduce((acc, odd) => acc * odd, 1);

    // Convert back to American odds
    const americanOdds = combinedDecimal >= 2
      ? Math.round((combinedDecimal - 1) * 100)
      : Math.round(-100 / (combinedDecimal - 1));

    return {
      decimal: combinedDecimal.toFixed(2),
      american: americanOdds,
      payout: (100 * combinedDecimal).toFixed(2)
    };
  };

  const handleExportParlay = async (bookmakerKey) => {
    setIsExporting(true);
    setError(null);

    try {
      // Check if all bets have parsed data with bookmaker info
      const betsWithBookmakers = bets.filter(bet => bet.parsedData?.bookmakers?.[bookmakerKey]);

      if (betsWithBookmakers.length === 0) {
        setError('No odds available for this bookmaker');
        setIsExporting(false);
        return;
      }

      if (betsWithBookmakers.length !== bets.length) {
        setError(`Only ${betsWithBookmakers.length}/${bets.length} bets available for ${BOOKMAKERS[bookmakerKey].name}`);
        setIsExporting(false);
        return;
      }

      if (bets.length === 1) {
        // Single bet - use the direct link
        const bookmakerData = bets[0].parsedData.bookmakers[bookmakerKey];
        const link = bookmakerData.direct_link || bookmakerData.link;
        if (link) {
          window.open(link, '_blank', 'noopener,noreferrer');
        } else {
          setError('No link available for this selection');
        }
      } else {
        // Multiple bets - create parlay link with proper hierarchy
        const parlaySelections = bets.map(bet => {
          const bookmakerData = bet.parsedData.bookmakers[bookmakerKey];

          // Build selection with proper SID hierarchy
          return {
            hierarchy: {
              marketId: bookmakerData.market_sid,
              selectionId: bookmakerData.selection_id,
              sids: {
                market: bookmakerData.market_sid,
                outcome: bookmakerData.selection_id
              }
            },
            market: { sid: bookmakerData.market_sid },
            outcome: {
              sid: bookmakerData.selection_id,
              link: bookmakerData.direct_link
            }
          };
        });

        const parlayLink = createParlayLink(bookmakerKey, parlaySelections);

        if (parlayLink) {
          // console.log('ParlayExporter - Opening parlay link:', parlayLink);
          window.open(parlayLink, '_blank', 'noopener,noreferrer');
        } else {
          // Fallback to bookmaker homepage with a message
          // console.log('ParlayExporter - Parlay construction failed, opening homepage');
          window.open(BOOKMAKERS[bookmakerKey].homepage, '_blank', 'noopener,noreferrer');
          setError('Opening bookmaker homepage - manual parlay creation needed');
        }
      }
    } catch (err) {
      console.error('Error exporting parlay:', err);
      setError('Failed to export picks');
    } finally {
      setIsExporting(false);
      setShowBookmakerMenu(false);
    }
  };

  // Get available bookmakers from bets
  const getAvailableBookmakers = () => {
    const bookmakerSet = new Set();
    bets.forEach(bet => {
      if (bet.parsedData?.bookmakers) {
        Object.keys(bet.parsedData.bookmakers).forEach(key => bookmakerSet.add(key));
      }
    });
    return Array.from(bookmakerSet);
  };

  const availableBookmakers = getAvailableBookmakers();
  const parlayOdds = isParlay ? calculateParlayOdds(selectedBookmaker) : null;

  if (bets.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowBookmakerMenu(!showBookmakerMenu)}
        disabled={isExporting || availableBookmakers.length === 0}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all relative overflow-hidden group ${
          isExporting || availableBookmakers.length === 0
            ? 'bg-white/5 text-white/30 cursor-not-allowed'
            : isParlay
            ? 'bg-gradient-to-r from-indigo-500/80 to-purple-500/80 hover:from-indigo-500 hover:to-purple-500 text-white shadow-md'
            : 'bg-white/10 hover:bg-white/20 text-white/80 hover:text-white'
        }`}
      >
        {/* Shimmer effect for parlay */}
        {isParlay && !isExporting && availableBookmakers.length > 0 && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity -translate-x-full group-hover:translate-x-full duration-700" />
        )}

        {isExporting ? (
          <div className="w-3 h-3 border-2 border-white/30 border-t-white animate-spin rounded-full" />
        ) : (
          <>
            {isParlay ? (
              <Zap className="w-3.5 h-3.5 relative z-10" />
            ) : (
              <ExternalLink className="w-3.5 h-3.5 relative z-10" />
            )}
            <span className="relative z-10">{isParlay ? 'Parlay' : 'Export'}</span>
            {availableBookmakers.length > 0 && (
              <ChevronDown className={`w-3 h-3 -mr-0.5 transition-transform relative z-10 ${
                showBookmakerMenu ? 'rotate-180' : ''
              }`} />
            )}
          </>
        )}
      </button>

      <AnimatePresence>
        {showBookmakerMenu && !isExporting && (
          <motion.div
            initial={{ opacity: 0, y: dropdownDirection === 'up' ? 10 : -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: dropdownDirection === 'up' ? 10 : -10, scale: 0.95 }}
            className={`absolute right-0 w-56 bg-slate-800 border border-slate-700/80 rounded-lg shadow-xl overflow-hidden z-50 ${
              dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            <div className="p-1">
              {availableBookmakers.map(key => {
                const bookmaker = BOOKMAKERS[key];
                if (!bookmaker) return null;

                // Check how many bets support this bookmaker
                const supportedBets = bets.filter(bet => bet.parsedData?.bookmakers?.[key]);
                const allSupported = supportedBets.length === bets.length;

                return (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedBookmaker(key);
                      handleExportParlay(key);
                    }}
                    onMouseEnter={() => setSelectedBookmaker(key)}
                    className={`w-full px-3 py-2 text-left hover:bg-slate-700 rounded transition-colors flex items-center justify-between group ${
                      !allSupported ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm text-white/90">{bookmaker.name}</span>
                      {!allSupported && (
                        <span className="text-[10px] text-yellow-400">
                          {supportedBets.length}/{bets.length} bets available
                        </span>
                      )}
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-white/50 group-hover:text-cyan-400 transition-colors" />
                  </button>
                );
              })}
              {availableBookmakers.length === 0 && (
                <div className="px-3 py-2 text-xs text-white/50">
                  Waiting for odds data...
                </div>
              )}
            </div>

            {/* Parlay odds calculator */}
            {isParlay && parlayOdds && (
              <div className="px-3 py-2.5 border-t border-white/10 bg-white/5">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs text-white/70 font-medium">
                      <Calculator className="w-3.5 h-3.5" />
                      <span>{bets.length}-Leg Parlay</span>
                    </div>
                    <div className="text-sm font-bold text-white/90">
                      {parlayOdds.american > 0 ? '+' : ''}{parlayOdds.american}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-white/40">$100 bet pays</span>
                    <span className="text-white/70 font-semibold">
                      ${parlayOdds.payout}
                    </span>
                  </div>
                  <div className="text-[10px] text-white/30 text-center pt-1">
                    {BOOKMAKERS[selectedBookmaker]?.name || 'FanDuel'}
                  </div>
                </div>
              </div>
            )}

            {/* Single bet info */}
            {!isParlay && (
              <div className="px-3 py-2.5 border-t border-white/10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-white/60 font-medium">
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Single Bet</span>
                  </div>
                  <div className="text-[10px] text-white/40 bg-white/5 px-2 py-0.5 rounded">
                    Quick place
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-full mt-1 right-0 text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-1 rounded"
        >
          {error}
        </motion.div>
      )}
    </div>
  );
};

export default ParlayExporter;
